import { PaperAirplane } from "@procore/core-icons";
import {
   Button,
   Page,
   Form,
   Typography,
   Dropdown,
   Modal,
   ZIndexProvider,
   Tooltip,
   useI18nContext,
   Box,
} from "@procore/core-react";
import React, { useState } from "react";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { useToastAlertContext } from "@procore/toast-alert";
import { CannedMessageType } from "@laborchart-modules/common/dist/rethink/schemas/enums/canned-messages";
import { timeOptions } from "@/lib/utils/timezones";
import { object, date } from "yup";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { AlertStore } from "@/stores/alert-store.core";
import type {
   AlertFormValues,
   AlertPayload,
   AssignmentTableBaseProps,
} from "./assignment-list-prop-types";
import { AssignmentSendAction } from "./assignment-list-prop-types";
import { getAlertContext, getModalContent } from "./assignment-list-helpers";
import type { NotifiableError } from "@bugsnag/js";
import { BUGSNAG_META_TAB, buildUserData } from "@/lib/utils/bugsnag-content-helper";
import { authManager } from "@/lib/managers/auth-manager";
import { useBugsnag } from "@/react/providers/BugsnagProvider";

export const AssignmentListSendAlert = (props: AssignmentTableBaseProps) => {
   const I18n = useI18nContext();
   const bugsnag = useBugsnag();
   const { groupId } = useGroupContext();
   const { showToast } = useToastAlertContext();
   const { tableApi, selectedRows, selectionInProgress, setManuallySelectedRowData } = props;

   const scheduleValidationSchema = object().shape({
      date: date().required(I18n.t("views.company.workforce_planning.validations.required_field")),
      time: object().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
   });

   const [isOpen, setIsOpen] = useState(false);
   const [selectedAction, setSelectedAction] = useState<AssignmentSendAction | null>(null);
   // check if any selected row has equipment assignment type
   const hasEquipment = selectedRows.some((row: any) => row.assignment_type === "equipment");

   const modalContent = getModalContent(selectedAction, I18n, selectedRows.length);

   const handleDropdownSelect = (selectedItem: AssignmentSendAction) => {
      setSelectedAction(selectedItem);
      setIsOpen(true);
   };

   const handleClose = () => {
      setIsOpen(false);
      setSelectedAction(null);
   };

   /* istanbul ignore next */
   const handleError = (error: NotifiableError) => {
      showToast.error(modalContent.error);
      bugsnag?.notify(error, (event) => {
         const user = authManager.authedUser();
         if (user) {
            event.addMetadata(
               BUGSNAG_META_TAB.USER_DATA,
               buildUserData(user, authManager.activePermission),
            );
         }
         event.severity = "error";
         event.context = "Helix Assignment List Alert Configuration";
         event.addMetadata(BUGSNAG_META_TAB.ASSIGNMENT, {
            data: JSON.stringify(selectedRows.map((row) => ({ id: row.id }))),
         });
      });
   };

   async function handleSubmit(values: AlertFormValues) {
      await postAlert(values);
   }

   async function postAlert(values?: AlertFormValues) {
      try {
         await Promise.all(
            selectedRows.map((assignment) => {
               const requestBody: AlertPayload = {
                  batch_id: assignment.id,
                  canned_type: CannedMessageType.ASSIGNMENT_NEW,
                  group_id: groupId === "my-groups" ? undefined : groupId,
                  context: getAlertContext(selectedAction),
               };

               // if form values are provided and not null/undefined, this will be available in case of scheduling
               if (values?.date) {
                  requestBody.detached_day = getDetachedDay(values.date);
               }
               if (values?.time) {
                  requestBody.time = values.time.id;
               }

               return AlertStore.processDefaultAlertForProject({ body: requestBody });
            }),
         );
         showToast.success(modalContent.success);
         tableApi?.deselectAll();
         tableApi?.refreshServerSide({ purge: true });
         setManuallySelectedRowData([]);
      } catch (error) {
         handleError(error as NotifiableError);
      } finally {
         handleClose();
      }
   }

   const renderFooterButtons = (onPrimaryClick: () => void, isSubmitting = false) => (
      <Modal.FooterButtons>
         <Button variant="tertiary" onClick={handleClose} data-testid="modal-cancel-button">
            {I18n.t("views.company.workforce_planning.cancel")}
         </Button>
         <Button variant="primary" disabled={isSubmitting} onClick={onPrimaryClick}>
            {modalContent.button}
         </Button>
      </Modal.FooterButtons>
   );

   const alertActions = [
      {
         key: AssignmentSendAction.SendImmediately,
         label: I18n.t("views.company.workforce_planning.assignment.bulk.alerts.send_immediately"),
      },
      {
         key: AssignmentSendAction.Schedule,
         label: I18n.t("views.company.workforce_planning.assignment.bulk.alerts.schedule"),
      },
      {
         key: AssignmentSendAction.SaveDraft,
         label: I18n.t("views.company.workforce_planning.assignment.bulk.alerts.save_draft"),
      },
   ];

   return (
      <>
         <ZIndexProvider>
            <Tooltip
               overlay={I18n.t(
                  "views.company.workforce_planning.assignment.bulk.alerts.equipment_assignment_selection_not_allowed_text",
               )}
               placement="right"
               trigger={hasEquipment ? "hover" : "none"}
            >
               <Box>
                  <Dropdown
                     icon={<PaperAirplane size="sm" />}
                     onSelect={(selectedItem) => {
                        handleDropdownSelect(selectedItem.item);
                     }}
                     variant="tertiary"
                     disabled={hasEquipment || selectionInProgress}
                     data-testid="alert-dropdown"
                  >
                     {alertActions.map((action) => (
                        <Dropdown.Item key={action.key} item={action.key}>
                           {action.label}
                        </Dropdown.Item>
                     ))}
                  </Dropdown>
               </Box>
            </Tooltip>
         </ZIndexProvider>
         {selectedAction && (
            <Modal open={isOpen} onClose={handleClose} style={{ maxWidth: "540px" }}>
               <Modal.Header onClose={handleClose}>{modalContent.header}</Modal.Header>

               {selectedAction === AssignmentSendAction.Schedule ? (
                  <Form
                     view="create"
                     onSubmit={handleSubmit}
                     initialValues={{
                        date: null,
                        time: null,
                     }}
                     validationSchema={scheduleValidationSchema}
                  >
                     {/* @ts-expect-error Core React Form uses Formik under the hood; this is valid implementation */}
                     {({ isSubmitting, submitForm }: FormikProps<any>) => (
                        <>
                           <Modal.Body>
                              <Page.Body
                                 style={{ width: "100%", marginBottom: "auto", padding: "0px" }}
                              >
                                 <Form.Row>
                                    <Form.DateSelect
                                       name="date"
                                       colStart={1}
                                       colWidth={5}
                                       label={I18n.t("views.company.workforce_planning.date")}
                                       required
                                    />
                                    <Form.Select
                                       colStart={6}
                                       colWidth={7}
                                       name="time"
                                       label={I18n.t("views.company.workforce_planning.time")}
                                       options={timeOptions}
                                       onSearch={false}
                                       required
                                       placeholder={I18n.t(
                                          "views.company.workforce_planning.select_option",
                                       )}
                                    />
                                 </Form.Row>
                              </Page.Body>
                           </Modal.Body>
                           <Modal.Footer>
                              {renderFooterButtons(submitForm, isSubmitting)}
                           </Modal.Footer>
                        </>
                     )}
                  </Form>
               ) : (
                  <>
                     <Modal.Body>
                        <Typography>{modalContent.description}</Typography>
                     </Modal.Body>
                     <Modal.Footer>{renderFooterButtons(() => postAlert())}</Modal.Footer>
                  </>
               )}
            </Modal>
         )}
      </>
   );
};
